import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  ICrateData,
  IReport,
  IReportData,
  IReportDataInventoryByCrate,
  IReportDataInventoryByPigeonholePosition,
  IReportPigeonholeItem,
  IReportPositionReference,
  IReportTarget,
  ISkuGroupsData,
  translateType
} from '../../../models/Report';
import formatDate from '../../helpers/format-date';
import { translateState } from '../../../models/Report';
import { InventoryTypesEnum } from '../../core/services/action.service';
import { toExcelFormat } from 'src/app/helpers/format-position';
import getName from 'src/app/helpers/format-name';
import { formatGroundPositionName, getGroundPositions } from 'src/app/helpers/get-ground-positions';
import { buildStockDetailsFromCrate, CrateDetailsWithInvoice, withCrateInvoice } from 'src/app/helpers/stock-details.helper';
import { StockDataSourceService } from 'src/app/core/data-sources/stock-data-source.service';
import { CratesDataSourceService } from 'src/app/core/data-sources/crates-data-source.service';
import { StockDetailsInterface } from '../stock-details-modal/stock-details-modal.component';
import { Crate } from 'src/models/Crate';

type ReportDetailsData = IReport['data'];
type ReportCloseState = 'APPROVED' | 'REJECTED' | 'CANCELED';
type GroundPositionOption = { name: string; groupId: string };
type RemovedPalletTableRow = {
  rfid: string;
  position: string;
  sku: string;
  name: string;
  quantity: string;
};

@Component({
  selector: 'app-report-details-modal',
  templateUrl: './report-details-modal.component.html',
  styleUrls: ['./report-details-modal.component.scss']
})
export class ReportDetailsModalComponent implements OnInit {
  inventoryTypesEnum = InventoryTypesEnum;
  items: ICrateData[] = [];
  palletItems: ISkuGroupsData[] = [];
  reportData: ReportDetailsData;
  reportTarget: IReportTarget | null = null;
  inventoryByCrateReportData: IReportDataInventoryByCrate | null = null;
  totalItems: number;
  productName = '';
  formattedGroundPositionGroupName = '';
  groundPositionName = '';
  removePallets = false;
  showPalletDetails = false;
  palletsToRemove: ICrateData[] = [];
  displayedColumns: string[] = ['rfid', 'position', 'sku', 'name', 'quantity'];
  removedPalletColumnsDefinition = [
    { key: 'rfid', text: 'Tag RFID', showTooltip: true },
    { key: 'position', text: 'Posição' },
    { key: 'sku', text: 'Código do Item', showTooltip: true },
    { key: 'name', text: 'Descrição', showTooltip: true },
    { key: 'quantity', text: 'Quantidade' }
  ];
  crateDetails: StockDetailsInterface | null = null;

  constructor(
    public dialogRef: MatDialogRef<ReportDetailsModalComponent>,
    @Inject(MAT_DIALOG_DATA) public report: IReport,
    private stockDataSourceService: StockDataSourceService,
    private cratesService: CratesDataSourceService
  ) { }

  async ngOnInit() {
    this.initializeReport();
    await this.loadReportMetadata();
  }

  private initializeReport(): void {
    this.reportData = this.report.data;
    this.reportTarget = this.getReportTarget();
    this.inventoryByCrateReportData = this.isInventoryByCrateReportData(this.reportData) ? this.reportData : null;
    this.setReportItemsAndTotal();
    this.markItemsByState();
    this.resetItemRemovalState();
  }

  private getReportTarget(): IReportTarget | null {
    if ('target' in this.reportData) {
      return this.reportData.target || null;
    }

    return null;
  }

  private async loadReportMetadata(): Promise<void> {
    await this.setGroundPositionGroupName();
    this.setGroundPositionName();
    this.setProductName();
  }

  private async setGroundPositionGroupName(): Promise<void> {
    if (this.reportData.type !== InventoryTypesEnum.INVENTORY_BY_GROUND_POSITION_GROUP) {
      return;
    }

    const targetNames = this.reportTarget?.groundPositionGroups?.map(group => group.name).filter((name): name is string => !!name);

    if (targetNames?.length) {
      this.formattedGroundPositionGroupName = targetNames.join(' / ');
      return;
    }

    const mapOptions = await this.stockDataSourceService.getGroundPosition(true);
    const groundPositions = getGroundPositions(mapOptions) as GroundPositionOption[];
    const groundPositionGroupIds = this.reportTarget?.groundPositionGroupIds || [];
    const groundPositionsNames = groundPositions
      .filter(position => groundPositionGroupIds.includes(position.groupId))
      .map(position => position.name);

    if (groundPositionsNames.length) {
      this.formattedGroundPositionGroupName = groundPositionsNames.join(' / ');
    }
  }

  private setGroundPositionName(): void {
    const isGroundPositionInventory =
      this.reportData.type === this.inventoryTypesEnum.INVENTORY_BY_GROUND_POSITION ||
      this.reportData.type === this.inventoryTypesEnum.INVENTORY_BY_PIGEONHOLE_POSITION;

    if (!isGroundPositionInventory) {
      return;
    }

    const groundPositions = this.reportTarget?.groundPositions || [];
    const positionNames = groundPositions
      .map(position => formatGroundPositionName(position, 'alias'))
      .filter((name): name is string => !!name);

    this.groundPositionName = positionNames.join(' / ') || '---';
  }

  private setProductName(): void {
    if (this.reportData.type !== InventoryTypesEnum.INVENTORY_BY_PRODUCT) {
      return;
    }

    const selectedSku = this.reportTarget?.skus?.[0];
    if (!selectedSku) {
      return;
    }

    for (const item of this.items) {
      const matchedProduct = item.skuGroups.find(product => product.sku === selectedSku);
      if (matchedProduct) {
        this.productName = matchedProduct.name;
        return;
      }
    }
  }

  private setReportItemsAndTotal(): void {
    switch (this.report.data.type) {
      case InventoryTypesEnum.INVENTORY_BY_PIGEONHOLE_POSITION:
        this.setPigeonholeReportData();
        return;
      case InventoryTypesEnum.PRODUCT_INVENTORY_BY_CRATE_TAG_RFID:
        this.setInventoryByCrateReportData();
        return;
      default:
        this.setDefaultReportData();
        return;
    }
  }

  private setPigeonholeReportData(): void {
    const reportData = this.reportData as IReportDataInventoryByPigeonholePosition;
    const packages = reportData.packages.map(item => ({ ...item, isPallet: false }));
    const pallets = reportData.pallets.map(item => ({ ...item, isPallet: true }));
    this.items = [...packages, ...pallets].map(item => this.normalizePigeonholeItem(item));
    this.palletItems = [];
    this.totalItems = this.report.total?.items || this.items.length;
  }

  private setInventoryByCrateReportData(): void {
    const reportData = this.inventoryByCrateReportData as IReportDataInventoryByCrate;
    const foundItems = reportData?.skuGroup?.found || [];
    const extraItems = reportData?.skuGroup?.extra || [];
    const missingItems = reportData?.skuGroup?.missing || [];
    this.items = [];
    this.palletItems = [...foundItems, ...extraItems, ...missingItems];
    this.totalItems = this.report.total?.items || foundItems.length + extraItems.length + missingItems.length;
  }

  private setDefaultReportData(): void {
    const reportData = this.reportData as IReportData;
    const foundItems = reportData?.found || [];
    const missingItems = reportData?.missing || [];
    const misplacedItems = reportData?.foundMisplaced || [];
    this.items = [...foundItems, ...missingItems, ...misplacedItems];
    this.palletItems = [];
    this.totalItems = this.report.total?.items || foundItems.length + misplacedItems.length + missingItems.length;
  }

  private resetItemRemovalState(): void {
    this.items.forEach(item => {
      item.removePallet = false;
    });
  }

  private normalizePigeonholeItem(item: IReportPigeonholeItem & { isPallet: boolean }): ICrateData {
    const expectedPosition = item?.expectedPosition || null;
    const foundPosition = item?.foundPosition || null;

    return {
      ...item,
      id: item.id || '',
      type: item.type || '',
      state: item.state || '',
      tagRfid: item.tagRfid || '',
      position: item.position || '',
      tenantId: item.tenantId || '',
      createdAt: item.createdAt || 0,
      updatedAt: item.updatedAt || 0,
      x: expectedPosition?.x ?? foundPosition?.x ?? item?.x ?? 0,
      y: expectedPosition?.y ?? foundPosition?.y ?? item?.y ?? 0,
      z: expectedPosition?.z ?? foundPosition?.z ?? item?.z ?? 0,
      skuGroups: item?.skuGroups || [],
      inconsistency: this.getPigeonholeInconsistency(item?.state, foundPosition),
      groundPositionGroupLevelName: expectedPosition?.groundPositionLevelName || item?.groundPositionGroupLevelName
    };
  }

  private getPigeonholeInconsistency(state?: string, foundPosition?: IReportPositionReference | null): ICrateData['inconsistency'] {
    if (state === 'FOUND_MISPLACED') {
      return {
        state,
        tagRfid: '',
        groundPositionGroupId: '',
        x: foundPosition?.x,
        y: foundPosition?.y,
        z: foundPosition?.z,
        groundPositionAlias: foundPosition?.groundPositionAlias,
        groundPositionLevelName: foundPosition?.groundPositionLevelName
      };
    }

    if (state === 'MISSING') {
      return { state, tagRfid: '', groundPositionGroupId: '' };
    }

    return null;
  }

  private markItemsByState(): void {
    const possibleArrayName = [ 'found', 'foundMisplaced', 'missing'];
    const reportData = this.reportData as Partial<IReportData>;

    for (const name of possibleArrayName) {
      const targetArray = reportData[name];
      if (targetArray?.length) {
        for (const element of targetArray) {
          Object.assign(element, { [name]: true });
        }
      }
    }
  }

  private isInventoryByCrateReportData(reportData: ReportDetailsData): reportData is IReportDataInventoryByCrate {
    return 'crate' in reportData && 'skuGroup' in reportData;
  }

  close(state: ReportCloseState): void {
    this.palletsToRemove = this.items.filter(item => item.removePallet);
    if (this.palletsToRemove.length && state === 'APPROVED') {
      this.removePallets = true;
      return;
    }
    this.dialogRef.close({ state, inventoryId: this.report.id, inventoryType: this.reportData.type });
  }

  confirmRemovePallets() {
    const tagsRfidToRemove = this.palletsToRemove.map(pallet => pallet.tagRfid);
    this.dialogRef.close({
      state: "APPROVED",
      inventoryId: this.report.id,
      tagsRfidToRemove,
      inventoryType: this.reportData.type
    });
  }

  getUserName(user): string {
    return getName(user);
  }

  translateTypeName(type: InventoryTypesEnum): string {
    return translateType(type);
  }

  async openPalletDetails(pallet: Pick<ICrateData, 'id'>): Promise<void> {
    const itemDetails = await this.getCrateDetailsById(pallet.id);
    if (!itemDetails) {
      return;
    }

    this.showPalletDetails = true;
    this.crateDetails = buildStockDetailsFromCrate(itemDetails.crate, itemDetails);
  }

  private async getCrateDetailsById(id: string): Promise<CrateDetailsWithInvoice | null> {
    return withCrateInvoice(await this.cratesService.getCrateById(id));
  }

  goBackToReport(): void {
    this.showPalletDetails = false;
  }

  onRelocateClicked($event: { crate: Crate }): void {
    this.dialogRef.close({
      openRelocateSidenav: true,
      crate: $event.crate
    });
  }

  onInventoryClicked($event: Crate): void {
    this.dialogRef.close({
      crate: $event,
      openPalletInventorySidenav: true,
      position: this.crateDetails?.itemDetails?.position
    });
  }

  getCrateState(state: IReport['state']): string {
    return translateState(state);
  }

  getFormattedDate(date: number): string {
    return formatDate(date) || '---';
  }

  getCoords(x: number, y: number): string {
    return toExcelFormat(x, y) || '---';
  }

  get removedPalletsDataSource(): RemovedPalletTableRow[] {
    return this.palletsToRemove.map(pallet => ({
      rfid: pallet.tagRfid || '---',
      position: this.getCoords(pallet.x, pallet.y),
      sku: pallet.skuGroups.map(item => item.sku).join(', ') || '---',
      name: pallet.skuGroups.map(item => item.name).join(', ') || '---',
      quantity: pallet.skuGroups.map(item => `${item.quantity}`).join(', ') || '---'
    }));
  }

}
